import { useState, useCallback } from 'react'
import { useStripe, useElements } from '@stripe/react-stripe-js'
import { API_ERROR_MAP } from '../main/types'
import getStripeErrorMessage from './getStripeErrorMessage'
import { PAYMENT_STAGE, logPaymentFailed } from 'uxcam-service'

const useStripeActions = () => {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)

  const createPaymentMethod = useCallback(async () => {
    if (!stripe || !elements)
      throw new Error(API_ERROR_MAP.AUTHNET_ERROR_OCCURRED)

    setIsProcessing(true)

    try {
      const { error: submitError } = await elements.submit()
      if (submitError) throw submitError

      const { error: paymentMethodError, paymentMethod } =
        await stripe.createPaymentMethod({
          elements,
        })

      if (paymentMethodError) throw paymentMethodError
      if (!paymentMethod) throw new Error()

      return paymentMethod.id
    } catch (error) {
      logPaymentFailed(PAYMENT_STAGE.PAYMENT_METHOD, error)
      const errorMessage = getStripeErrorMessage(error)
      throw new Error(errorMessage)
    } finally {
      setIsProcessing(false)
    }
  }, [stripe, elements])

  const confirmSetupIntent = useCallback(
    async (clientSecret) => {
      if (!stripe || !elements)
        throw new Error(API_ERROR_MAP.AUTHNET_ERROR_OCCURRED)

      setIsProcessing(true)

      try {
        const { error: submitError } = await elements.submit()
        if (submitError) throw submitError

        const { error: setupError, setupIntent } = await stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        })

        if (setupError) throw setupError
        if (!setupIntent) throw new Error()

        return setupIntent.payment_method
      } catch (error) {
        logPaymentFailed(PAYMENT_STAGE.SETUP_INTENT_CONFIRM, error)
        const errorMessage = getStripeErrorMessage(error)
        throw new Error(errorMessage)
      } finally {
        setIsProcessing(false)
      }
    },
    [stripe, elements],
  )

  return { isProcessing, createPaymentMethod, confirmSetupIntent }
}

export default useStripeActions
